import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { change } from "redux-form";

import { isNone } from "~/common/assert";
import {
  CO_TAG_FULL_3_STEPS_CBCT,
  CO_TAG_TEEN_3_STEPS_CBCT,
  CourseId,
  is3DPROCourse,
  isAdditionalAlignersCourse,
  isOneArchIncludedInCourse,
  isRetainersCourse,
  Status,
} from "~/common/courses";
import { remoteLog } from "~/common/logging";
import { getLastCorrection } from "~/common/patient";
import { Arch } from "~/common/prescription";
import { canOrderTestPlastic } from "~/common/user";
import { MaterialOptions } from "~/components/patient/patient_shared/material_options";
import { TestPlaticOptions } from "~/components/patient/patient_shared/test_plastic_options";
import {
  isCBCTIncludedInCourse,
  isCBCTServiceAvailableForCourse,
  type TCourseInstallment,
} from "~/slices/course-installment";
import { addInstructions, removeInstructions } from "~/slices/instructions";
import { selectCorrectionFormValues } from "~/slices/redux-form";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";

import {
  ChildrenShortTooltip,
  ChildrenV3Tooltip,
  CourseRadio,
  CourseRadioGroup,
  DiagnosisTextArea,
  ScanUploadCTWarning,
} from "../patient_new/patient_new_course";

const mapStateToProps = (state: RootState) => {
  return {
    user: state.user,
    instructions: state.instructions,
    formValues: selectCorrectionFormValues(state),
  };
};

const mapDispatchToProps = {
  addInstruction: addInstructions,
  removeInstructions,
  change,
};

type PatientUpdateCourseProps = PropsFromRedux & {
  patient: TPatient;
  courseInstallment: TCourseInstallment;
  setDirty(key: string, data: unknown): void;
};

class PatientUpdateCourse extends Component<PatientUpdateCourseProps> {
  constructor(props: PatientUpdateCourseProps) {
    super(props);
    this.addCondition = this.addCondition.bind(this);
    this.selectCourse = this.selectCourse.bind(this);
    this.selectDeepCBCTAnalysis = this.selectDeepCBCTAnalysis.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patient_update_course");
  }

  componentDidMount() {
    const { patient } = this.props;

    if (!isNone(patient.course.course_id)) {
      this.selectCourse(patient.course.course_id);
    }

    if (
      patient.deep_cbct ||
      [CO_TAG_FULL_3_STEPS_CBCT, CO_TAG_TEEN_3_STEPS_CBCT].includes(
        patient.course.course_option_tag,
      )
    ) {
      this.props.addInstruction({ deep_cbct: true });
    }

    this.props.addInstruction({ condition: patient.diagnosis });
    const lastCorrection = getLastCorrection(patient);

    if (canOrderTestPlastic(this.props.user) && lastCorrection.test_plastic != null) {
      this.props.addInstruction({ test_plastic: lastCorrection.test_plastic });
    }
  }

  selectCourse(course_id: CourseId) {
    const { patient, instructions, courseInstallment, formValues } = this.props;

    this.props.addInstruction({ course_id });
    this.props.setDirty("course_id", course_id);

    if (
      instructions.course_id != course_id &&
      instructions.payment_option_id &&
      !(instructions.payment_option_id in courseInstallment[course_id]) &&
      patient.can_edit_payment_option
    ) {
      this.props.addInstruction({ payment_option_id: null });
    }

    if (formValues?.arch == Arch.BOTH && isOneArchIncludedInCourse(course_id)) {
      this.props.change("correction", "arch", null);
    }

    if (!isCBCTServiceAvailableForCourse(courseInstallment, course_id)) {
      this.props.addInstruction({ deep_cbct: false });
    }

    if (isRetainersCourse(course_id)) {
      this.props.addInstruction({ sber_credit: false });
    }
  }

  selectDeepCBCTAnalysis(data: boolean) {
    this.props.addInstruction({ deep_cbct: data });
    this.props.setDirty("deep_cbct", data);
  }

  addCondition(data: string) {
    this.props.addInstruction({ condition: data });
    this.props.setDirty("diagnosis", data);
  }

  render() {
    const { patient, courseInstallment } = this.props;
    const { course_id = null, deep_cbct } = this.props.instructions;

    const lastCorrection = getLastCorrection(patient);
    const showTestPlasticOptions =
      canOrderTestPlastic(this.props.user) &&
      (patient.status == Status.UNFILLED_CASE || lastCorrection.test_plastic != null);

    return (
      <div id="course-section">
        <h3 className="block" style={{ fontWeight: "900" }}>
          <FormattedMessage id="BLOCKHEAD_INSTRUCTIONS" />
        </h3>

        <PatientUpdateCourseOptions
          patient={patient}
          selectedCourse={course_id}
          courseInstallment={courseInstallment}
          onCourseChange={this.selectCourse}
        />

        {/*
         * If you use AnimatePresence here, then if you switch from course that displays this warning to
         * retainers, then material options have a nasty buggy layout shift on the right of the screen.
         * So i decided to remove AnimatePresence alltogether, feels better for that edge case.
         */}
        {(course_id != null && isCBCTIncludedInCourse(courseInstallment, course_id)) ||
        deep_cbct ? (
          <ScanUploadCTWarning />
        ) : null}

        {isRetainersCourse(course_id) || isAdditionalAlignersCourse(course_id) ? null : (
          <DiagnosisTextArea
            diagnosis={patient.diagnosis}
            onChange={(e) => this.addCondition(e.target.value)}
          />
        )}

        {isAdditionalAlignersCourse(course_id) ? null : <MaterialOptions />}

        {showTestPlasticOptions ? (
          <TestPlaticOptions
            value={this.props.instructions.test_plastic}
            onValueChange={(test_plastic) => this.props.addInstruction({ test_plastic })}
          />
        ) : null}

        {course_id != null && isCBCTServiceAvailableForCourse(courseInstallment, course_id) ? (
          <div id="deep-cbct-section">
            <h5 className="block" id="deep-cbct-label" style={{ fontWeight: "900" }}>
              <FormattedMessage id="ADDITIONAL_INSTRUCTIONS" />
            </h5>

            <label
              htmlFor="deep-cbct-value"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <input
                id="deep-cbct-value"
                style={{ marginTop: 0 }}
                type="checkbox"
                name="deep_cbct"
                disabled={
                  patient.status != Status.UNFILLED_CASE &&
                  (patient.deep_cbct == true ||
                    [CO_TAG_FULL_3_STEPS_CBCT, CO_TAG_TEEN_3_STEPS_CBCT].includes(
                      patient.course.course_option_tag,
                    ))
                }
                checked={deep_cbct == true}
                onChange={(e) => this.selectDeepCBCTAnalysis(e.currentTarget.checked)}
              />
              <FormattedMessage id="C_DEEP_CBCT_ANALYSIS_ITEM" />
            </label>
          </div>
        ) : null}
      </div>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(PatientUpdateCourse);

function PatientUpdateCourseOptions(props: {
  patient: TPatient;
  selectedCourse: number | null;
  courseInstallment: TCourseInstallment;
  onCourseChange(course: CourseId): void;
}) {
  const { patient } = props;

  const isNotChildrenOrChildrenV3 =
    patient.course.course_id != CourseId.CHILDREN ||
    patient.course.course_option_tag == "CHILDREN_V3";

  return (
    <CourseRadioGroup>
      <CourseRadio
        {...props}
        course={CourseId.CBCT}
        image={<img src="/img/deepcbct.png" alt="" width={80} height={42} />}
      />
      {patient.status != Status.UNFILLED_CASE && is3DPROCourse(patient.course.course_id) ? (
        <CourseRadio
          {...props}
          course={CourseId.PRO}
          image={<img src="/img/pro_smile_button.svg" alt="" width={105} />}
        />
      ) : null}
      <CourseRadio {...props} isNew course={CourseId.LIMITLESS} />
      <CourseRadio {...props} course={CourseId.FULL} />
      {patient.can_set_teen_course ? <CourseRadio {...props} course={CourseId.TEEN} /> : null}
      <CourseRadio {...props} course={CourseId.SHORT} />
      <CourseRadio {...props} course={CourseId.SINGLE_ARCH} />
      <CourseRadio {...props} course={CourseId.SUPER_SHORT} />
      <CourseRadio {...props} course={CourseId.PLAN_3D} />
      <CourseRadio
        {...props}
        course={CourseId.CHILDREN}
        tooltip={isNotChildrenOrChildrenV3 ? <ChildrenV3Tooltip /> : null}
      />
      <CourseRadio {...props} course={CourseId.CHILDREN_SHORT} tooltip={<ChildrenShortTooltip />} />
      <CourseRadio {...props} course={CourseId.RETAINERS} />
      <CourseRadio {...props} course={CourseId.ADDITIONAL_ALIGNERS} />
    </CourseRadioGroup>
  );
}
